import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Search, Trash2, FileText, Pause, Play, XCircle, Zap, Loader2, Send, BookTemplate, Copy, Download, Package, FileSignature } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["all", "active", "pending_acceptance", "paused", "cancelled", "expired"];
const TYPES = ["all", "hosting", "support", "development", "service_agreement", "subscription", "license", "other"];
const INTERVALS = ["monthly", "quarterly", "yearly"];
const INTERVAL_LABELS = { monthly: "Månadsvis", quarterly: "Kvartalsvis", yearly: "Årsvis" };

const STATUS_COLORS = {
  active: "bg-green-500/10 text-green-400 border-green-500/20",
  pending_acceptance: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  paused: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  cancelled: "bg-red-500/10 text-red-400 border-red-500/20",
  expired: "bg-neutral-500/10 text-[#857973] border-neutral-500/20",
};

const TYPE_LABELS = {
  hosting: "Hosting", support: "Support", development: "Development",
  installation: "Installation", service_agreement: "Service Agreement",
  subscription: "Subscription", license: "License", project: "Project", other: "Other",
};

export default function AgreementsPage() {
  const [agreements, setAgreements] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [editAgreement, setEditAgreement] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const emptyForm = { customer_id: "", title: "", description: "", type: "project", amount: 0, currency: "SEK", billing_interval: "monthly", start_date: "", end_date: "", binding_period: "", notice_period: "", auto_invoice: true, auto_charge: false, acceptance_required: true, notes: "", items: [] };
  const [form, setForm] = useState({ ...emptyForm });
  const [tplForm, setTplForm] = useState({ name: "", title: "", description: "", type: "hosting", amount: 0, currency: "EUR", billing_interval: "monthly", binding_period: "", notice_period: "", auto_invoice: true, auto_charge: false, notes: "" });
  const [editTpl, setEditTpl] = useState(null);
  const [articles, setArticles] = useState([]);
  const [showArticles, setShowArticles] = useState(false);
  const [artForm, setArtForm] = useState({ name: "", title: "", description: "", unit_price: 0, currency: "SEK", recurrence_type: "one_time", billing_interval: "monthly" });
  const [editArt, setEditArt] = useState(null);
  const [sendingFor, setSendingFor] = useState(null);

  const fetchData = useCallback(async () => {
    const [agrRes, custRes, statsRes, tplRes, artRes] = await Promise.all([
      axios.get(`${API}/admin/agreements`, { params: { status: filterStatus, type: filterType }, withCredentials: true }),
      axios.get(`${API}/admin/customers-list`, { withCredentials: true }),
      axios.get(`${API}/admin/agreements/stats`, { withCredentials: true }),
      axios.get(`${API}/admin/agreement-templates`, { withCredentials: true }),
      axios.get(`${API}/admin/agreement-articles`, { withCredentials: true }),
    ]);
    setAgreements(agrRes.data);
    setCustomers(custRes.data);
    setStats(statsRes.data);
    setTemplates(tplRes.data);
    setArticles(artRes.data);
  }, [filterStatus, filterType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const custName = (id) => customers.find(c => c.id === id)?.company_name || "—";

  const applyTemplate = (tpl) => {
    setForm(prev => ({
      ...prev,
      title: tpl.title, description: tpl.description, type: tpl.type,
      amount: tpl.amount, currency: tpl.currency, billing_interval: tpl.billing_interval,
      binding_period: tpl.binding_period, notice_period: tpl.notice_period,
      auto_invoice: tpl.auto_invoice, auto_charge: tpl.auto_charge, notes: tpl.notes,
    }));
  };

  const saveTpl = async (e) => {
    e.preventDefault();
    if (editTpl) {
      await axios.put(`${API}/admin/agreement-templates/${editTpl.id}`, tplForm, { withCredentials: true });
    } else {
      await axios.post(`${API}/admin/agreement-templates`, tplForm, { withCredentials: true });
    }
    setTplForm({ name: "", title: "", description: "", type: "hosting", amount: 0, currency: "EUR", billing_interval: "monthly", binding_period: "", notice_period: "", auto_invoice: true, auto_charge: false, notes: "" });
    setEditTpl(null);
    fetchData();
  };

  const deleteTpl = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await axios.delete(`${API}/admin/agreement-templates/${id}`, { withCredentials: true });
    fetchData();
  };

  const filtered = agreements.filter(a =>
    !search || a.title?.toLowerCase().includes(search.toLowerCase()) || custName(a.customer_id).toLowerCase().includes(search.toLowerCase())
  );

  const monthlyAmount = (a) => {
    const amt = a.amount || 0;
    if (a.billing_interval === "yearly") return amt / 12;
    if (a.billing_interval === "quarterly") return amt / 3;
    return amt;
  };

  const createAgreement = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/admin/agreements`, { ...form, amount: Number(form.amount) }, { withCredentials: true });
    setShowCreate(false);
    setForm({ ...emptyForm });
    fetchData();
  };

  const updateAgreement = async (id, updates) => {
    await axios.put(`${API}/admin/agreements/${id}`, updates, { withCredentials: true });
    fetchData();
    if (editAgreement?.id === id) {
      const { data } = await axios.get(`${API}/admin/agreements`, { params: { status: filterStatus, type: filterType }, withCredentials: true });
      setEditAgreement(data.find(a => a.id === id));
    }
  };

  const deleteAgreement = async (id) => {
    if (!window.confirm("Delete this agreement?")) return;
    await axios.delete(`${API}/admin/agreements/${id}`, { withCredentials: true });
    fetchData();
    setEditAgreement(null);
  };

  const generateInvoices = async () => {
    setGenerating(true);
    setGenResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/agreements/generate-invoices`, {}, { withCredentials: true });
      setGenResult(data);
      fetchData();
    } catch (err) {
      setGenResult({ error: "Failed to generate invoices" });
    } finally {
      setGenerating(false);
    }
  };

  // ==== Items helpers ====
  const addItem = (preset) => {
    const it = preset || { title: "", description: "", quantity: 1, unit_price: 0, recurrence_type: "one_time", billing_interval: "monthly" };
    setForm(p => ({ ...p, items: [...(p.items || []), it] }));
  };
  const updateItem = (i, patch) => {
    setForm(p => ({ ...p, items: p.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  };
  const removeItem = (i) => {
    setForm(p => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));
  };
  const addItemFromArticle = (artId) => {
    const a = articles.find(x => x.id === artId);
    if (!a) return;
    addItem({
      title: a.title || a.name,
      description: a.description || "",
      quantity: 1,
      unit_price: a.unit_price || 0,
      currency: a.currency,
      recurrence_type: a.recurrence_type || "one_time",
      billing_interval: a.billing_interval || "monthly",
    });
  };

  // ==== Computed totals (live in modal) ====
  const computeTotals = (items, currency = "SEK") => {
    let one = 0, mo = 0;
    (items || []).forEach(it => {
      const qty = Number(it.quantity || 1);
      const price = Number(it.unit_price || 0);
      const sub = qty * price;
      if ((it.recurrence_type || "one_time") === "recurring") {
        const interval = (it.billing_interval || "monthly").toLowerCase();
        mo += interval === "monthly" ? sub : sub / 12;
      } else {
        one += sub;
      }
    });
    return { one_time: one, monthly: mo, yearly_discounted: mo * 10, yearly_savings: mo * 2, currency };
  };
  const fmtMoney = (n, ccy) => `${(Number(n) || 0).toLocaleString("sv-SE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`;

  // ==== Send for signing / Download PDF / Articles CRUD ====
  const sendForSigning = async (id) => {
    if (!window.confirm("Skicka avtalet till kunden för digital signering?")) return;
    setSendingFor(id);
    try {
      const { data } = await axios.post(`${API}/admin/agreements/${id}/send-for-acceptance`, {}, { withCredentials: true });
      alert(`Skickat! Länk: ${data.accept_url}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Misslyckades skicka");
    }
    setSendingFor(null);
  };

  const downloadAgreementPdf = async (id, title) => {
    try {
      const res = await axios.get(`${API}/admin/agreements/${id}/pdf`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `avtal-${(title || id).replace(/\s+/g, "_").slice(0, 40)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      alert("Kunde inte ladda ner PDF: " + (err.response?.statusText || err.message));
    }
  };

  const saveArticle = async (e) => {
    e.preventDefault();
    if (editArt) {
      await axios.put(`${API}/admin/agreement-articles/${editArt.id}`, artForm, { withCredentials: true });
    } else {
      await axios.post(`${API}/admin/agreement-articles`, artForm, { withCredentials: true });
    }
    setArtForm({ name: "", title: "", description: "", unit_price: 0, currency: "SEK", recurrence_type: "one_time", billing_interval: "monthly" });
    setEditArt(null);
    fetchData();
  };
  const deleteArticle = async (id) => {
    if (!window.confirm("Ta bort denna artikelmall?")) return;
    await axios.delete(`${API}/admin/agreement-articles/${id}`, { withCredentials: true });
    fetchData();
  };

  return (
    <div data-testid="agreements-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Agreements & Services</h1>
          <p className="text-sm text-[#857973]">{agreements.length} agreements</p>
        </div>
        <div className="flex gap-2">
          {stats?.pending_invoices > 0 && (
            <button data-testid="generate-invoices-btn" onClick={generateInvoices} disabled={generating}
              className="flex items-center gap-2 bg-green-500/10 border border-green-500/30 text-green-400 px-4 py-2 rounded-sm text-sm font-medium hover:bg-green-500/20 transition-all disabled:opacity-50">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              Generate {stats.pending_invoices} invoice{stats.pending_invoices !== 1 ? "s" : ""}
            </button>
          )}
          <button onClick={() => setShowTemplates(true)} data-testid="manage-templates-btn" className="flex items-center gap-2 bg-[#1E3050]/40 border border-[#1E3050] text-[#857973] px-4 py-2 rounded-sm text-sm font-medium hover:text-white transition-all">
            <Copy className="w-4 h-4" /> Templates
          </button>
          <button onClick={() => setShowArticles(true)} data-testid="manage-articles-btn" className="flex items-center gap-2 bg-[#1E3050]/40 border border-[#1E3050] text-[#857973] px-4 py-2 rounded-sm text-sm font-medium hover:text-white transition-all">
            <Package className="w-4 h-4" /> Artiklar ({articles.length})
          </button>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <button data-testid="create-agreement-btn" className="flex items-center gap-2 bg-[#F59073] text-black px-4 py-2 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all">
                <Plus className="w-4 h-4" /> New Agreement
              </button>
            </DialogTrigger>
          <DialogContent className="bg-[#0F1F3A] border-[#1E3050] text-white max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-outfit">Create Agreement</DialogTitle></DialogHeader>
            <form onSubmit={createAgreement} className="space-y-4 mt-4">
              {/* Template picker */}
              {templates.length > 0 && (
                <div>
                  <label className="font-mono text-[10px] text-[#857973] mb-1.5 block">USE TEMPLATE</label>
                  <div className="flex flex-wrap gap-1.5">
                    {templates.map(t => (
                      <button key={t.id} type="button" onClick={() => applyTemplate(t)} data-testid={`tpl-${t.id}`}
                        className="px-3 py-1.5 rounded-sm text-xs bg-[#1E3050]/40 border border-[#1E3050] text-[#857973] hover:text-[#F59073] hover:border-[#F59073]/30 transition-colors">
                        {t.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <select required value={form.customer_id} onChange={e => setForm(p => ({ ...p, customer_id: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="create-agreement-customer">
                <option value="">Select customer...</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
              <input required placeholder="Agreement title (e.g. Hosting Plan)" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="create-agreement-title" />
              <textarea placeholder="Description" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none resize-none" rows={2} />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="create-agreement-type">
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select value={form.billing_interval} onChange={e => setForm(p => ({ ...p, billing_interval: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
                  {INTERVALS.map(i => <option key={i} value={i}>{INTERVAL_LABELS[i]}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="font-mono text-[10px] text-[#857973] mb-1 block">AMOUNT</label>
                  <input type="number" required value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="create-agreement-amount" />
                </div>
                <div>
                  <label className="font-mono text-[10px] text-[#857973] mb-1 block">CURRENCY</label>
                  <select value={form.currency || "SEK"} onChange={e => setForm(p => ({ ...p, currency: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
                    <option value="SEK">SEK</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono text-[10px] text-[#857973] mb-1 block">START DATE</label>
                  <input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                </div>
              </div>
              {/* === Items / Avtalsrader === */}
              <div className="bg-[#0A1628] border border-[#1E3050] rounded-sm p-3" data-testid="items-section">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Package className="w-3.5 h-3.5 text-[#F59073]" />
                    <p className="text-sm text-white font-medium">Avtalsrader</p>
                    <span className="text-[10px] text-[#857973]">({(form.items || []).length})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {articles.length > 0 && (
                      <select onChange={e => { if (e.target.value) { addItemFromArticle(e.target.value); e.target.value = ""; } }}
                        data-testid="article-picker"
                        className="bg-[#050505] border border-[#1E3050] text-[#D9A872] text-xs px-2 py-1.5 rounded-sm">
                        <option value="">+ Från artikelbank…</option>
                        {articles.map(a => <option key={a.id} value={a.id}>{a.name} ({a.recurrence_type === "recurring" ? "löpande" : "engång"})</option>)}
                      </select>
                    )}
                    <button type="button" onClick={() => addItem()} data-testid="add-item-btn"
                      className="text-xs text-[#F59073] hover:underline flex items-center gap-1">+ Lägg till rad</button>
                  </div>
                </div>
                {(form.items || []).length === 0 && (
                  <p className="text-[10px] text-[#857973]">Lägg till engångs- och löpande rader (utveckling, hosting m.m). Du kan blanda i samma avtal.</p>
                )}
                <div className="space-y-2">
                  {(form.items || []).map((it, i) => (
                    <div key={i} className="border border-[#1E3050] rounded-sm p-2 space-y-2 bg-[#050505]/40" data-testid={`item-${i}`}>
                      <div className="flex gap-2">
                        <input value={it.title} onChange={e => updateItem(i, { title: e.target.value })}
                          placeholder="Titel (t.ex. Hosting Basic)"
                          className="flex-1 bg-[#050505] border border-[#1E3050] text-white px-2 py-1 rounded-sm text-xs outline-none" />
                        <button type="button" onClick={() => removeItem(i)}
                          className="text-[#857973] hover:text-red-400 px-2">×</button>
                      </div>
                      <textarea value={it.description || ""} onChange={e => updateItem(i, { description: e.target.value })}
                        placeholder="Kort beskrivning (visas på avtalet)"
                        className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1 rounded-sm text-xs outline-none resize-none" rows={2} />
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <label className="text-[9px] font-mono text-[#857973] block">ANTAL</label>
                          <input type="number" step="0.5" value={it.quantity} onChange={e => updateItem(i, { quantity: Number(e.target.value) })}
                            className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1 rounded-sm text-xs outline-none" />
                        </div>
                        <div>
                          <label className="text-[9px] font-mono text-[#857973] block">Á-PRIS</label>
                          <input type="number" step="0.01" value={it.unit_price} onChange={e => updateItem(i, { unit_price: Number(e.target.value) })}
                            className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1 rounded-sm text-xs outline-none" />
                        </div>
                        <div>
                          <label className="text-[9px] font-mono text-[#857973] block">TYP</label>
                          <select value={it.recurrence_type} onChange={e => updateItem(i, { recurrence_type: e.target.value })}
                            className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1 rounded-sm text-xs">
                            <option value="one_time">Engång</option>
                            <option value="recurring">Löpande</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-[9px] font-mono text-[#857973] block">INTERVALL</label>
                          <select value={it.billing_interval || "monthly"} disabled={it.recurrence_type !== "recurring"}
                            onChange={e => updateItem(i, { billing_interval: e.target.value })}
                            className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1 rounded-sm text-xs disabled:opacity-40">
                            <option value="monthly">Månadsvis</option>
                            <option value="yearly">Årsvis</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Live totals */}
                {(form.items || []).length > 0 && (() => {
                  const t = computeTotals(form.items, form.currency || "SEK");
                  return (
                    <div className="mt-3 pt-3 border-t border-[#1E3050] space-y-1 text-xs">
                      {t.one_time > 0 && <div className="flex justify-between"><span className="text-[#857973]">Engång (totalt)</span><span className="text-white font-mono">{fmtMoney(t.one_time, t.currency)}</span></div>}
                      {t.monthly > 0 && (
                        <>
                          <div className="flex justify-between"><span className="text-[#857973]">Löpande (per månad)</span><span className="text-white font-mono">{fmtMoney(t.monthly, t.currency)}/mån</span></div>
                          <div className="flex justify-between"><span className="text-green-400">Vid årsfakturering (10×)</span><span className="text-green-400 font-mono">{fmtMoney(t.yearly_discounted, t.currency)}/år · spara {fmtMoney(t.yearly_savings, t.currency)}</span></div>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] text-[#857973] mb-1 block">END DATE (optional)</label>
                  <input type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                </div>
                <div>
                  <label className="font-mono text-[10px] text-[#857973] mb-1 block">BINDING PERIOD</label>
                  <input placeholder="e.g. 12 months" value={form.binding_period} onChange={e => setForm(p => ({ ...p, binding_period: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] text-[#857973] mb-1 block">NOTICE PERIOD</label>
                  <input placeholder="e.g. 3 months" value={form.notice_period} onChange={e => setForm(p => ({ ...p, notice_period: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.auto_invoice} onChange={e => setForm(p => ({ ...p, auto_invoice: e.target.checked }))} className="rounded-sm border-[#1E3050] bg-[#0A1628] text-[#F59073]" />
                    <span className="text-sm text-[#857973]">Auto-faktura</span>
                  </label>
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.auto_charge} onChange={e => setForm(p => ({ ...p, auto_charge: e.target.checked }))} className="rounded-sm border-[#1E3050] bg-[#0A1628] text-[#F59073]" data-testid="auto-charge-toggle" />
                    <span className="text-sm text-[#857973]">Auto-debitera kort</span>
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.acceptance_required} onChange={e => setForm(p => ({ ...p, acceptance_required: e.target.checked }))} className="rounded-sm border-[#1E3050] bg-[#0A1628] text-[#F59073]" data-testid="acceptance-required-toggle" />
                  <span className="text-sm text-[#857973]">Require customer acceptance by email</span>
                </label>
              </div>
              <textarea placeholder="Internal notes" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none resize-none" rows={2} />
              <button type="submit" data-testid="create-agreement-submit" className="w-full bg-[#F59073] text-black py-2.5 rounded-sm text-sm font-medium">Create Agreement</button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats bar */}
      {genResult && !genResult.error && (
        <div data-testid="generate-result" className="mb-6 bg-green-500/10 border border-green-500/20 rounded-sm p-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-green-400 font-medium">
              {genResult.generated_count > 0
                ? `Generated ${genResult.generated_count} draft invoice${genResult.generated_count !== 1 ? "s" : ""}`
                : "No invoices due for generation"}
            </p>
            {genResult.invoices?.length > 0 && (
              <div className="mt-2 space-y-1">
                {genResult.invoices.map((inv, i) => (
                  <p key={i} className="text-xs text-[#857973]">
                    {inv.agreement_title}: {inv.amount?.toLocaleString()} &rarr; Next: {inv.next_invoice_date}
                  </p>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => setGenResult(null)} className="text-[#857973] hover:text-white">&times;</button>
        </div>
      )}
      {genResult?.error && (
        <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-sm p-4 flex items-center justify-between">
          <p className="text-sm text-red-400">{genResult.error}</p>
          <button onClick={() => setGenResult(null)} className="text-[#857973] hover:text-white">&times;</button>
        </div>
      )}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4">
            <span className="font-mono text-[10px] tracking-[0.15em] text-[#857973]">ACTIVE</span>
            <div className="font-outfit font-bold text-2xl text-white mt-1">{stats.active_count}</div>
          </div>
          <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4">
            <span className="font-mono text-[10px] tracking-[0.15em] text-[#857973]">SERVICES MRR</span>
            <div className="font-outfit font-bold text-2xl text-[#F59073] mt-1">{Object.entries(stats.mrr_by_currency || {}).map(([c, v]) => `${v.toLocaleString()} ${c}`).join(" + ") || "0"}</div>
          </div>
          {Object.entries(stats.by_type || {}).slice(0, 2).map(([type, data]) => (
            <div key={type} className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4">
              <span className="font-mono text-[10px] tracking-[0.15em] text-[#857973]">{TYPE_LABELS[type]?.toUpperCase() || type.toUpperCase()}</span>
              <div className="font-outfit font-bold text-2xl text-white mt-1">{data.count}</div>
              <span className="text-xs text-[#857973]">{Object.entries(data.mrr_by_currency || {}).map(([c, v]) => `${v.toLocaleString()} ${c}`).join(" + ")}/mo</span>
            </div>
          ))}
          {stats.pending_invoices > 0 && !Object.keys(stats.by_type || {}).length && (
            <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-sm p-4">
              <span className="font-mono text-[10px] tracking-[0.15em] text-yellow-400">PENDING</span>
              <div className="font-outfit font-bold text-2xl text-yellow-400 mt-1">{stats.pending_invoices}</div>
              <span className="text-xs text-[#857973]">invoices to generate</span>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#857973]" />
          <input data-testid="agreements-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search agreements..."
            className="w-full bg-[#0F1F3A] border border-[#1E3050] text-white pl-10 pr-4 py-2.5 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {STATUSES.map(s => (
            <button key={s} onClick={() => setFilterStatus(s)}
              className={`px-3 py-2 rounded-sm text-xs font-mono transition-colors ${filterStatus === s ? "bg-[#F59073]/10 text-[#F59073] border border-[#F59073]/30" : "bg-[#0F1F3A] text-[#857973] border border-[#1E3050] hover:text-white"}`}>
              {s}
            </button>
          ))}
        </div>
        <div className="flex gap-1 flex-wrap">
          {TYPES.map(t => (
            <button key={t} onClick={() => setFilterType(t)}
              className={`px-3 py-2 rounded-sm text-xs font-mono transition-colors ${filterType === t ? "bg-purple-500/10 text-purple-400 border border-purple-500/30" : "bg-[#0F1F3A] text-[#857973] border border-[#1E3050] hover:text-white"}`}>
              {TYPE_LABELS[t] || t}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1E3050]">
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">AGREEMENT</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">CUSTOMER</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">TYPE</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">AMOUNT</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">INTERVAL</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">STATUS</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">NEXT INVOICE</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">MRR</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id} className="border-b border-[#1E3050] hover:bg-[#1E3050]/20 cursor-pointer transition-colors" onClick={() => setEditAgreement(a)}>
                  <td className="px-4 py-3">
                    <span className="text-sm text-white">{a.title}</span>
                    {a.auto_invoice && <span className="ml-2 text-[10px] text-[#F59073] font-mono">AUTO</span>}
                    {a.auto_charge && <span className="ml-1 text-[10px] text-green-400 font-mono">KORT</span>}
                    {a.acceptance_required && !a.accepted_at && <span className="ml-1 text-[10px] text-yellow-400 font-mono">PENDING</span>}
                    {a.accepted_at && <span className="ml-1 text-[10px] text-green-400 font-mono">ACCEPTED</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#857973]">{custName(a.customer_id)}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs bg-[#1E3050]/40 text-[#857973] px-2 py-0.5 rounded-sm font-mono">{TYPE_LABELS[a.type] || a.type}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-white font-mono">
                    {a.amount?.toLocaleString()} {a.currency}
                    <span className="text-[10px] text-[#857973] ml-1">/{a.billing_interval === "yearly" ? "år" : a.billing_interval === "quarterly" ? "kv" : "mån"}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#857973] font-mono">{INTERVAL_LABELS[a.billing_interval] || a.billing_interval}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-sm font-mono border ${STATUS_COLORS[a.status] || "bg-[#1E3050]/40 text-[#857973] border-[#1E3050]"}`}>{a.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono">
                    {a.auto_invoice && a.next_invoice_date ? (
                      <span className={a.next_invoice_date <= new Date().toISOString().slice(0, 10) ? "text-yellow-400" : "text-[#857973]"}>
                        {a.next_invoice_date}
                      </span>
                    ) : <span className="text-[#1E3050]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#F59073] font-mono">{Math.round(monthlyAmount(a)).toLocaleString()} {a.currency || "EUR"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end" onClick={e => e.stopPropagation()}>
                      <button onClick={() => sendForSigning(a.id)} disabled={sendingFor === a.id || !a.accept_token}
                        title="Skicka för signering" data-testid={`send-sign-${a.id}`}
                        className="text-[#857973] hover:text-[#D9A872] disabled:opacity-40">
                        {sendingFor === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSignature className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => downloadAgreementPdf(a.id, a.title)} title="Ladda ner PDF" data-testid={`pdf-${a.id}`}
                        className="text-[#857973] hover:text-[#F59073]"><Download className="w-3.5 h-3.5" /></button>
                      <button onClick={() => deleteAgreement(a.id)} title="Ta bort" className="text-[#857973] hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-[#857973]">No agreements found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Sheet */}
      {editAgreement && (
        <div className="fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setEditAgreement(null)} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-[#0F1F3A] border-l border-[#1E3050] overflow-y-auto z-10 p-6">
            <div className="flex justify-between mb-6">
              <div>
                <h2 className="font-outfit font-bold text-xl text-white">{editAgreement.title}</h2>
                <p className="text-xs text-[#857973] font-mono">{custName(editAgreement.customer_id)}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => deleteAgreement(editAgreement.id)} className="text-[#857973] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                <button onClick={() => setEditAgreement(null)} className="text-[#857973] hover:text-white text-xl">&times;</button>
              </div>
            </div>

            <div className="space-y-5">
              {/* Amount & interval */}
              <div className="bg-black/40 border border-[#1E3050] rounded-sm p-4">
                <div className="flex items-baseline justify-between">
                  <div>
                    <span className="font-mono text-[10px] text-[#857973] block">BELOPP</span>
                    <span className="font-outfit font-bold text-3xl text-white">{editAgreement.amount?.toLocaleString()}</span>
                    <span className="text-[#857973] text-sm ml-1">{editAgreement.currency}</span>
                    <span className="text-[#857973] text-sm ml-1">/ {editAgreement.billing_interval === "yearly" ? "år" : editAgreement.billing_interval === "quarterly" ? "kvartal" : "månad"}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-mono text-[10px] text-[#857973] block">FAKTURERAS</span>
                    <span className="text-sm text-white">{INTERVAL_LABELS[editAgreement.billing_interval] || editAgreement.billing_interval}</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-[#1E3050] flex justify-between">
                  <span className="text-xs text-[#857973]">Motsvarar per månad</span>
                  <span className="text-sm text-[#F59073] font-mono">{Math.round(monthlyAmount(editAgreement)).toLocaleString()} {editAgreement.currency || "SEK"}/mån</span>
                </div>
                {editAgreement.auto_invoice && editAgreement.next_invoice_date && (
                  <div className="mt-3 pt-3 border-t border-[#1E3050] flex justify-between items-center">
                    <span className="text-xs text-[#857973]">Next invoice date</span>
                    <span className={`text-sm font-mono ${editAgreement.next_invoice_date <= new Date().toISOString().slice(0, 10) ? "text-yellow-400" : "text-neutral-300"}`}>
                      {editAgreement.next_invoice_date}
                      {editAgreement.next_invoice_date <= new Date().toISOString().slice(0, 10) && (
                        <span className="ml-2 text-[10px] bg-yellow-500/10 text-yellow-400 px-1.5 py-0.5 rounded-sm">DUE</span>
                      )}
                    </span>
                  </div>
                )}
              </div>

              {/* Details grid */}
              <div className="grid grid-cols-2 gap-4">
                <div><span className="font-mono text-[10px] text-[#857973] block mb-1">TYPE</span><span className="text-sm text-white">{TYPE_LABELS[editAgreement.type] || editAgreement.type}</span></div>
                <div><span className="font-mono text-[10px] text-[#857973] block mb-1">AUTO-INVOICE</span><span className="text-sm text-white">{editAgreement.auto_invoice ? "Yes" : "No"}</span></div>
                <div><span className="font-mono text-[10px] text-[#857973] block mb-1">AUTO-DEBITERING</span><span className="text-sm text-white">{editAgreement.auto_charge ? "Ja (kort)" : "Nej"}</span></div>
                <div><span className="font-mono text-[10px] text-[#857973] block mb-1">START DATE</span><span className="text-sm text-neutral-300">{editAgreement.start_date || "—"}</span></div>
                <div><span className="font-mono text-[10px] text-[#857973] block mb-1">END DATE</span><span className="text-sm text-neutral-300">{editAgreement.end_date || "Ongoing"}</span></div>
                <div><span className="font-mono text-[10px] text-[#857973] block mb-1">BINDING</span><span className="text-sm text-neutral-300">{editAgreement.binding_period || "—"}</span></div>
                <div><span className="font-mono text-[10px] text-[#857973] block mb-1">NOTICE PERIOD</span><span className="text-sm text-neutral-300">{editAgreement.notice_period || "—"}</span></div>
              </div>

              {editAgreement.description && (
                <div><span className="font-mono text-[10px] text-[#857973] block mb-1">DESCRIPTION</span><p className="text-sm text-neutral-300">{editAgreement.description}</p></div>
              )}

              {editAgreement.notes && (
                <div><span className="font-mono text-[10px] text-[#857973] block mb-1">NOTES</span><p className="text-sm text-neutral-300">{editAgreement.notes}</p></div>
              )}

              {/* Acceptance status */}
              {editAgreement.acceptance_required && (
                <div className={`rounded-sm p-4 border ${editAgreement.accepted_at ? "bg-green-500/5 border-green-500/20" : "bg-yellow-500/5 border-yellow-500/20"}`}>
                  <span className="font-mono text-[10px] text-[#857973] block mb-1">CUSTOMER ACCEPTANCE</span>
                  {editAgreement.accepted_at ? (
                    <div>
                      <span className="text-sm text-green-400 font-medium">Accepted</span>
                      <span className="text-xs text-[#857973] ml-2">{editAgreement.accepted_at.slice(0, 10)} by {editAgreement.accepted_by || "customer"}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-yellow-400">Awaiting acceptance — send agreement to customer</span>
                  )}
                </div>
              )}

              {/* Status controls */}
              <div>
                <span className="font-mono text-[10px] text-[#857973] block mb-2">STATUS</span>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.filter(s => s !== "all").map(s => (
                    <button key={s} onClick={() => updateAgreement(editAgreement.id, { status: s })} data-testid={`agreement-status-${s}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-mono border transition-colors ${
                        editAgreement.status === s ? (STATUS_COLORS[s] || "") : "bg-[#1E3050]/40 text-[#857973] border-[#1E3050] hover:text-white"
                      }`}>
                      {s === "active" && <Play className="w-3 h-3" />}
                      {s === "paused" && <Pause className="w-3 h-3" />}
                      {s === "cancelled" && <XCircle className="w-3 h-3" />}
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Send to customer */}
              <div className="pt-3 border-t border-[#1E3050]">
                <button
                  onClick={async () => {
                    try {
                      const { data } = await axios.post(`${API}/admin/agreements/${editAgreement.id}/send`, { origin_url: window.location.origin }, { withCredentials: true });
                      alert(`Agreement sent to ${data.to}`);
                      fetchData();
                    } catch (err) { alert(err.response?.data?.detail || "Failed to send"); }
                  }}
                  data-testid="send-agreement-btn"
                  className="w-full flex items-center justify-center gap-2 bg-[#F59073]/10 text-[#F59073] border border-[#F59073]/20 py-2.5 rounded-sm text-sm font-medium hover:bg-[#F59073]/20 transition-all"
                >
                  <Send className="w-4 h-4" /> {editAgreement.acceptance_required && !editAgreement.accepted_at ? "Send for acceptance" : "Send agreement to customer"}
                </button>
                {editAgreement.sent_at && (
                  <p className="text-xs text-[#857973] text-center mt-2">Last sent: {editAgreement.sent_at.slice(0, 10)} to {editAgreement.sent_to}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Templates Modal */}
      {showTemplates && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => { setShowTemplates(false); setEditTpl(null); }} />
          <div className="relative bg-[#0F1F3A] border border-[#1E3050] rounded-sm w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 z-10" data-testid="templates-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-outfit font-bold text-lg text-white">Agreement Templates</h2>
              <button onClick={() => { setShowTemplates(false); setEditTpl(null); }} className="text-[#857973] hover:text-white text-xl">&times;</button>
            </div>

            {/* Template form */}
            <form onSubmit={saveTpl} className="bg-[#0A1628] border border-[#1E3050] rounded-sm p-4 mb-6 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Template name (e.g. 'Standard Hosting')" value={tplForm.name} onChange={e => setTplForm(p => ({ ...p, name: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="tpl-name" />
                <input placeholder="Agreement title" value={tplForm.title} onChange={e => setTplForm(p => ({ ...p, title: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
              </div>
              <textarea placeholder="Description" value={tplForm.description} onChange={e => setTplForm(p => ({ ...p, description: e.target.value }))} rows={2} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none resize-none" />
              <div className="grid grid-cols-4 gap-3">
                <select value={tplForm.type} onChange={e => setTplForm(p => ({ ...p, type: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <input type="number" step="0.01" placeholder="Amount" value={tplForm.amount || ""} onChange={e => setTplForm(p => ({ ...p, amount: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                <select value={tplForm.currency} onChange={e => setTplForm(p => ({ ...p, currency: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
                  <option value="EUR">EUR</option><option value="SEK">SEK</option>
                </select>
                <select value={tplForm.billing_interval} onChange={e => setTplForm(p => ({ ...p, billing_interval: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none">
                  {INTERVALS.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Binding period (e.g. 12 months)" value={tplForm.binding_period} onChange={e => setTplForm(p => ({ ...p, binding_period: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
                <input placeholder="Notice period (e.g. 3 months)" value={tplForm.notice_period} onChange={e => setTplForm(p => ({ ...p, notice_period: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
              </div>
              <textarea placeholder="Notes / terms" value={tplForm.notes} onChange={e => setTplForm(p => ({ ...p, notes: e.target.value }))} rows={2} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none resize-none" />
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={tplForm.auto_invoice} onChange={e => setTplForm(p => ({ ...p, auto_invoice: e.target.checked }))} className="rounded-sm" /><span className="text-sm text-[#857973]">Auto-faktura</span></label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={tplForm.auto_charge} onChange={e => setTplForm(p => ({ ...p, auto_charge: e.target.checked }))} className="rounded-sm" /><span className="text-sm text-[#857973]">Auto-debitera</span></label>
              </div>
              <button type="submit" data-testid="save-template-btn" className="w-full bg-[#F59073] text-black py-2 rounded-sm text-sm font-medium">
                {editTpl ? "Update Template" : "Save Template"}
              </button>
            </form>

            {/* Existing templates */}
            <div className="space-y-2">
              {templates.map(t => (
                <div key={t.id} className="flex items-center justify-between bg-[#0A1628] border border-[#1E3050] rounded-sm p-4 hover:border-[#1E3050] transition-all">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm text-white font-medium">{t.name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-mono ${STATUS_COLORS.active || "bg-[#1E3050]/40 text-[#857973]"}`}>{TYPE_LABELS[t.type] || t.type}</span>
                    </div>
                    <span className="text-xs text-[#857973]">{t.amount} {t.currency} / {t.billing_interval}{t.title ? ` — "${t.title}"` : ""}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => { setEditTpl(t); setTplForm({ name: t.name, title: t.title || "", description: t.description || "", type: t.type, amount: t.amount, currency: t.currency, billing_interval: t.billing_interval, binding_period: t.binding_period || "", notice_period: t.notice_period || "", auto_invoice: t.auto_invoice ?? true, auto_charge: t.auto_charge ?? false, notes: t.notes || "" }); }}
                      className="text-[#857973] hover:text-[#F59073] transition-colors text-xs px-2 py-1 border border-[#1E3050] rounded-sm">Edit</button>
                    <button onClick={() => deleteTpl(t.id)} className="text-[#857973] hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
              {templates.length === 0 && <p className="text-center text-[#857973] text-sm py-6">No templates yet. Create one above.</p>}
            </div>
          </div>
        </div>
      )}

      {/* Articles modal */}
      {showArticles && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowArticles(false)} />
          <div className="relative bg-[#0F1F3A] border border-[#1E3050] rounded-sm w-full max-w-2xl p-6 z-10 max-h-[90vh] overflow-y-auto" data-testid="articles-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-outfit text-lg text-white">Artikelbank</h3>
              <button onClick={() => setShowArticles(false)} className="text-[#857973] hover:text-white text-xl">×</button>
            </div>
            <p className="text-xs text-[#857973] mb-4">Spara fördefinierade rader (utveckling, hosting, support …) som du återanvänder när du skapar avtal.</p>
            <form onSubmit={saveArticle} className="bg-[#0A1628] border border-[#1E3050] rounded-sm p-3 mb-4 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input value={artForm.name} onChange={e => setArtForm(p => ({ ...p, name: e.target.value }))} required
                  placeholder="Internt namn (t.ex. Hosting Basic)"
                  className="bg-[#050505] border border-[#1E3050] text-white px-2 py-1.5 rounded-sm text-sm outline-none" data-testid="art-name" />
                <input value={artForm.title} onChange={e => setArtForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="Titel (visas på avtalet)"
                  className="bg-[#050505] border border-[#1E3050] text-white px-2 py-1.5 rounded-sm text-sm outline-none" />
              </div>
              <textarea value={artForm.description} onChange={e => setArtForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Beskrivning (visas på avtalet)" rows={2}
                className="w-full bg-[#050505] border border-[#1E3050] text-white px-2 py-1.5 rounded-sm text-sm outline-none resize-none" />
              <div className="grid grid-cols-4 gap-2">
                <input type="number" step="0.01" value={artForm.unit_price} onChange={e => setArtForm(p => ({ ...p, unit_price: Number(e.target.value) }))}
                  placeholder="Á-pris" className="bg-[#050505] border border-[#1E3050] text-white px-2 py-1.5 rounded-sm text-sm outline-none" />
                <select value={artForm.currency} onChange={e => setArtForm(p => ({ ...p, currency: e.target.value }))}
                  className="bg-[#050505] border border-[#1E3050] text-white px-2 py-1.5 rounded-sm text-sm">
                  <option value="SEK">SEK</option><option value="EUR">EUR</option>
                </select>
                <select value={artForm.recurrence_type} onChange={e => setArtForm(p => ({ ...p, recurrence_type: e.target.value }))}
                  className="bg-[#050505] border border-[#1E3050] text-white px-2 py-1.5 rounded-sm text-sm">
                  <option value="one_time">Engång</option><option value="recurring">Löpande</option>
                </select>
                <select value={artForm.billing_interval} disabled={artForm.recurrence_type !== "recurring"}
                  onChange={e => setArtForm(p => ({ ...p, billing_interval: e.target.value }))}
                  className="bg-[#050505] border border-[#1E3050] text-white px-2 py-1.5 rounded-sm text-sm disabled:opacity-40">
                  <option value="monthly">Månadsvis</option><option value="yearly">Årsvis</option>
                </select>
              </div>
              <button type="submit" data-testid="save-art-btn"
                className="w-full bg-[#F59073] text-black py-2 rounded-sm text-sm font-medium">{editArt ? "Spara ändringar" : "Lägg till artikel"}</button>
              {editArt && <button type="button" onClick={() => { setEditArt(null); setArtForm({ name: "", title: "", description: "", unit_price: 0, currency: "SEK", recurrence_type: "one_time", billing_interval: "monthly" }); }}
                className="w-full text-xs text-[#857973]">Avbryt</button>}
            </form>
            <div className="space-y-2">
              {articles.map(a => (
                <div key={a.id} className="flex items-center justify-between bg-[#0A1628] border border-[#1E3050] rounded-sm p-3" data-testid={`art-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm text-white">{a.name}</p>
                      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${a.recurrence_type === "recurring" ? "bg-[#F59073]/10 text-[#F59073] border border-[#F59073]/30" : "bg-[#1E3050]/40 text-[#857973]"}`}>
                        {a.recurrence_type === "recurring" ? `LÖPANDE/${a.billing_interval}` : "ENGÅNG"}
                      </span>
                      <span className="text-xs text-[#D9A872] font-mono">{fmtMoney(a.unit_price, a.currency)}</span>
                    </div>
                    {a.description && <p className="text-xs text-[#857973] mt-0.5 truncate">{a.description}</p>}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => { setEditArt(a); setArtForm({ name: a.name, title: a.title || "", description: a.description || "", unit_price: a.unit_price, currency: a.currency, recurrence_type: a.recurrence_type, billing_interval: a.billing_interval || "monthly" }); }}
                      className="text-xs text-[#D9A872] hover:underline px-2">Ändra</button>
                    <button onClick={() => deleteArticle(a.id)} className="text-[#857973] hover:text-red-400 px-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
              {articles.length === 0 && <p className="text-center text-[#857973] text-sm py-6">Inga artiklar än. Lägg till en ovan.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
